import {useState} from 'react'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import {DELAY_REASONS, OTHER_DELAY_ID, lockedDelayReasonIds} from '@/features/trip-delay/tripDelayReasons'

/**
 * WHY ARE YOU LATE? — step A of the delay flow, and the only part of it in a modal.
 *
 * ── ⚠️ IT SENDS NOTHING ───────────────────────────────────────────────────────────
 * This modal picks a reason and hands it back. The request happens later, in the chat,
 * once the passenger has also chosen how long — and that is deliberate rather than an
 * accident of layout: the minutes are the part the driver acts on, so asking for them in
 * the thread keeps the driver's own reply one scroll away instead of behind a dialog.
 *
 * ── ⚠️ REASONS RETIRE AS THE WALK PROGRESSES ──────────────────────────────────────
 * Once a passenger has reported being held at customs, immigration is behind them and
 * cannot be the next thing to hold them up. Locked rows stay VISIBLE and dimmed rather
 * than disappearing: a list that changes length between openings reads as a bug, and the
 * dimmed row is also the record of what they already told the driver. See
 * `lockedDelayReasonIds`.
 *
 * ── ⚠️ "OTHER" SENDS THE PASSENGER'S WORDS, NOT THE WORD "OTHER" ──────────────────
 * The driver's app prints `reason_text` verbatim. Sending the row label there would show a
 * driver "Other Delay" and nothing else, which is worse than no reason at all.
 */
/**
 * ⚠️ IT STARTS FRESH BY BEING REMOUNTED, NOT BY BEING RESET. The caller changes this
 * component's `key` each time it opens, so the step, the selection and the free text are
 * simply new state rather than three fields cleared by an effect.
 *
 * The effect version had a real bug in it as well as being the longer road: clearing when
 * `open` goes false wipes the fields while the modal is still animating away, which the
 * reader watches happen.
 */
export default function DelayReasonModal({open, onClose, onPicked, delays}) {
    const [step, setStep] = useState('reason')
    const [selected, setSelected] = useState(null)
    const [otherText, setOtherText] = useState('')

    const locked = lockedDelayReasonIds(delays)
    const isOther = selected === OTHER_DELAY_ID
    const trimmed = otherText.trim()
    const canContinue = step === 'reason' ? !!selected : trimmed.length > 0

    const advance = () => {
        if (step === 'reason') {
            if (!selected) return
            /* "Other" is the only row that asks a second question; every other one is
               already a complete answer. */
            if (isOther) { setStep('otherText'); return }
            const reason = DELAY_REASONS.find((r) => r.id === selected)
            onPicked({id: reason.id, label: reason.label})
            return
        }
        if (!trimmed) return
        onPicked({id: OTHER_DELAY_ID, label: trimmed})
    }

    return (
        <Modal isOpen={open} onClose={onClose}>
            <ModalHeader
                title="Experiencing a Delay?"
                onClose={onClose}
                /* Only the text step has somewhere to go back TO. On the first step a back
                   arrow would be a second close button wearing the wrong icon. */
                onBack={step === 'otherText' ? () => setStep('reason') : undefined}
            />

            <div className="flex flex-col gap-5">
                <p className="mb-0! text-(--text-secondary)">
                    Immigration, customs, or lost baggage, let your driver know.
                </p>

                {step === 'reason' ? (
                    <div className="flex flex-col">
                        {DELAY_REASONS.map((reason) => {
                            const disabled = locked.has(reason.id)
                            return (
                                <button
                                    key={reason.id}
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => setSelected(reason.id)}
                                    className={`flex cursor-pointer items-center gap-3 border-0 border-b border-(--border-color) bg-transparent px-0 py-4 text-left last:border-b-0 disabled:cursor-default ${
                                        disabled ? 'opacity-40' : ''
                                    }`}
                                >
                                    {/* A radio, drawn: single choice, and a checkbox here
                                        would invite a passenger to report two reasons at
                                        once for one window. */}
                                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                                        selected === reason.id ? 'border-(--text-primary)' : 'border-(--border-color)'
                                    }`}>
                                        {selected === reason.id && (
                                            <span className="h-2.5 w-2.5 rounded-full bg-(--text-primary)"/>
                                        )}
                                    </span>
                                    <span className="flex-1 font-medium text-(--text-primary)">{reason.label}</span>
                                </button>
                            )
                        })}
                    </div>
                ) : (
                    <textarea
                        value={otherText}
                        onChange={(e) => setOtherText(e.target.value)}
                        rows={4}
                        autoFocus
                        placeholder="Tell your driver what's going on…"
                        className="w-full resize-none rounded-(--radius-lg) border border-(--border-color) bg-(--bg-white) px-4 py-3 text-sm outline-none"
                    />
                )}

                <button
                    type="button"
                    onClick={advance}
                    disabled={!canContinue}
                    className="min-h-14 w-full cursor-pointer rounded-full border-0 bg-(--text-primary) px-6 font-semibold text-(--bg-white) disabled:cursor-default disabled:opacity-40"
                >
                    Continue
                </button>
            </div>
        </Modal>
    )
}

export {DelayReasonModal}
